import { API_BASE } from '../apiBase';
// src/finances/ProjectsFinances.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
function ProjectsFinances() {
const navigate = useNavigate();
const [projects, setProjects] = useState([]);
const [allInvoices, setAllInvoices] = useState([]);
const [allPayments, setAllPayments] = useState([]);
const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);
const [searchTerm, setSearchTerm] = useState('');
useEffect(() => {
const fetchData = async () => {
const token = localStorage.getItem('token');
if (!token) {
navigate('/login');
return;
      }
try {
setLoading(true);
const projectsRes = await axios.get(`${API_BASE}/projects`, {
headers: { Authorization: token }
        });
setProjects(projectsRes.data);
const invoicesRes = await axios.get(`${API_BASE}/invoices`, {
headers: { Authorization: token }
        });
setAllInvoices(invoicesRes.data);
try {
const paymentsRes = await axios.get(`${API_BASE}/payments`, {
headers: { Authorization: token }
          });
setAllPayments(paymentsRes.data || []);
} catch (payErr) {
console.warn("Could not load payments:", payErr.message);
setAllPayments([]);
}
      } catch (err) {
console.error('Failed to load data:', err);
setError('Could not load project financial data.');
      } finally {
setLoading(false);
      }
    };
fetchData();
  }, [navigate]);
const filteredProjects = useMemo(() => {
if (!searchTerm.trim()) return projects;
const lower = searchTerm.toLowerCase();
return projects.filter(p =>
p.poNumber?.toLowerCase().includes(lower) ||
p.title?.toLowerCase().includes(lower) ||
p.customer?.name?.toLowerCase().includes(lower) ||
p.status?.toLowerCase().includes(lower)
    );
  }, [projects, searchTerm]);
const totals = useMemo(() => {
let invoiced = 0;
let paid = 0;
let outstanding = 0;
filteredProjects.forEach(proj => {
const projectInvoices = allInvoices.filter(inv =>
inv.project?._id === proj._id || inv.project === proj._id
      );
const nonDraftInvoices = projectInvoices.filter(inv => inv.status !== 'Draft');
const projInvoiced = nonDraftInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
const projPaidFromInvoices = nonDraftInvoices
        .filter(inv => inv.status === 'Paid')
        .reduce((sum, inv) => sum + (inv.total || 0), 0);
const projectPayments = allPayments.filter(p => 
p.project?._id === proj._id || p.project === proj._id
);
const projPaidFromPayments = projectPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
const projPaid = projPaidFromInvoices + projPaidFromPayments;
// FIXED: Never show negative outstanding
const projOutstanding = Math.max(0, projInvoiced - projPaid);
invoiced += projInvoiced;
paid += projPaid;
outstanding += projOutstanding;
    });
return { invoiced, paid, outstanding };
  }, [filteredProjects, allInvoices, allPayments]);
const statusColors = {
'Prospective': 'bg-purple-600/80 text-white',
'Captured': 'bg-blue-600/80 text-white',
'Active': 'bg-cyan-600/80 text-white',
'Completed': 'bg-green-600/80 text-white',
'Lost': 'bg-red-600/80 text-white',
  };
const handleRowClick = (proj) => {
navigate(`/projects/${proj._id}?tab=finances`);
  };
if (loading) {
return <div className="min-h-screen bg-gray-950 text-zinc-100 p-8">Loading project financials...</div>;
  }
if (error) {
return <div className="min-h-screen bg-gray-950 text-red-400 p-8">{error}</div>;
  }
return (
<div className="min-h-screen bg-gray-950 text-zinc-100 p-8 space-y-10">
<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
<h1 className="text-3xl font-bold tracking-tight">Project Financials (All Projects)</h1>
<button
className="px-6 py-3 bg-indigo-700/90 hover:bg-indigo-600 text-white rounded-xl font-medium shadow-md transition-all hover:shadow-lg hover:scale-[1.02]"
onClick={() => alert('Export to PDF/Excel coming soon!')}
>
          Export Report
</button>
</div>
<div className="max-w-xl">
<input
type="text"
placeholder="Search by PO number, title, or status..."
value={searchTerm}
onChange={(e) => setSearchTerm(e.target.value)}
className="w-full px-5 py-3.5 bg-gray-900 border border-gray-800 rounded-xl text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60 focus:ring-2 focus:ring-indigo-600/20 transition-all"
/>
</div>
<div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
<table className="min-w-full divide-y divide-gray-800/60">
<thead className="bg-gray-950/30">
<tr>
<th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">PO Number</th>
<th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Project Title</th>
<th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Invoiced</th>
<th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Paid</th>
<th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Outstanding</th>
<th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Profit Margin</th>
<th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Status</th>
</tr>
</thead>
<tbody className="divide-y divide-gray-800/40">
{filteredProjects.length === 0 ? (
<tr>
<td colSpan={7} className="px-8 py-20 text-center text-zinc-500 text-lg">
                  No financial data found. Try adjusting your search.
</td>
</tr>
            ) : (
filteredProjects.map((proj) => {
const projectInvoices = allInvoices.filter(inv =>
inv.project?._id === proj._id || inv.project === proj._id
                );
const nonDraftInvoices = projectInvoices.filter(inv => inv.status !== 'Draft');
const invoiced = nonDraftInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
const paidFromInvoices = nonDraftInvoices
                  .filter(inv => inv.status === 'Paid')
                  .reduce((sum, inv) => sum + (inv.total || 0), 0);
const projectPayments = allPayments.filter(p => 
p.project?._id === proj._id || p.project === proj._id
);
const paidFromPayments = projectPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
const paid = paidFromInvoices + paidFromPayments;
const outstanding = Math.max(0, invoiced - paid);   // Fixed here
const profitMargin = invoiced > 0 ? '18%' : '—';
return (
<tr
key={proj._id}
className="bg-gray-900 hover:bg-gray-800/50 hover:shadow-sm transition-all duration-200 cursor-pointer group"
onClick={() => handleRowClick(proj)}
>
<td className="px-8 py-6 whitespace-nowrap text-sm font-medium text-zinc-100">
{proj.poNumber || '—'}
</td>
<td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-300">
{proj.title || '—'}
</td>
<td className="px-8 py-6 whitespace-nowrap text-sm text-green-400">
                      ${invoiced.toLocaleString() || '—'}
</td>
<td className="px-8 py-6 whitespace-nowrap text-sm text-green-400">
                      ${paid.toLocaleString() || '—'}
</td>
<td className="px-8 py-6 whitespace-nowrap text-sm text-red-400">
                      ${outstanding.toLocaleString() || '—'}
</td>
<td className="px-8 py-6 whitespace-nowrap text-sm font-medium text-zinc-100">
{profitMargin}
</td>
<td className="px-8 py-6 whitespace-nowrap">
<span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
statusColors[proj.status] || 'bg-gray-600/80 text-white'
}`}>
{proj.status || '—'}
</span>
</td>
</tr>
                );
              })
            )}
</tbody>
<tfoot className="bg-gray-950/50 font-bold">
<tr>
<td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-100">Totals</td>
<td className="px-8 py-6 whitespace-nowrap"></td>
<td className="px-8 py-6 whitespace-nowrap text-sm text-green-400">
                ${totals.invoiced.toLocaleString() || '—'}
</td>
<td className="px-8 py-6 whitespace-nowrap text-sm text-green-400">
                ${totals.paid.toLocaleString() || '—'}
</td>
<td className="px-8 py-6 whitespace-nowrap text-sm text-red-400">
                ${totals.outstanding.toLocaleString() || '—'}
</td>
<td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-100">—</td>
<td className="px-8 py-6 whitespace-nowrap"></td>
</tr>
</tfoot>
</table>
</div>
<p className="text-center text-zinc-500 italic mt-8">
        Owner/Superadmin view only • Financial data across all projects
</p>
</div>
  );
}
export default ProjectsFinances;